import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { ContactCTA } from "../components/ContactCTA";
import { asList, mediaUrl, useFetch } from "../lib/api";
import { STATUS, ease } from "../lib/content";

const Chip = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${active ? "border-teal bg-teal text-mist" : "border-teal/25 text-teal hover:border-teal"}`}
  >
    {children}
  </button>
);

export const ProductCard = ({ p, i }) => (
  <motion.div layout initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.6, ease }}>
    <Link to={`/products/${p.slug}`} data-testid={`product-card-${i}`} className="group block">
      <div className="arch relative aspect-[4/5] overflow-hidden bg-mist">
        {p.image && <img src={mediaUrl(p.image)} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.07]" />}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-teal-ink/70 to-transparent p-5 pt-16">
          <StatusBadge status={p.status} testId={`product-status-${i}`} />
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-mist text-teal transition-transform duration-500 group-hover:rotate-45">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-moss">{p.category}</p>
      <h3 className="mt-2 font-display text-3xl font-normal tracking-tight text-teal">{p.name}</h3>
      {p.tagline && <p className="mt-1 font-display text-lg font-light italic text-teal/80">{p.tagline}</p>}
      <p className="mt-4 text-[15px] leading-relaxed text-teal-ink/80">{p.summary}</p>
    </Link>
  </motion.div>
);

export default function Products() {
  const { data, loading } = useFetch("/products");
  const products = asList(data);
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const filtered = products.filter((p) => (status === "all" || p.status === status) && (category === "all" || p.category === category));

  return (
    <main data-testid="products-page">
      <PageHeader
        testId="products-header"
        eyebrow="Products"
        lines={["Biological crop", "protection, in the pipeline."]}
        intro="An overview of the biological crop protection solutions we supply and develop, from what is available today to what is coming next."
      />
      <section className="bg-paper py-16 sm:py-24">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col gap-6 border-b border-teal/15 pb-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2" data-testid="status-filters">
              <Chip active={status === "all"} onClick={() => setStatus("all")} testId="status-filter-all">All</Chip>
              {Object.entries(STATUS).filter(([k]) => k !== "coming_soon").map(([k, v]) => (
                <Chip key={k} active={status === k} onClick={() => setStatus(k)} testId={`status-filter-${k}`}>
                  {v.label}
                </Chip>
              ))}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="category-filters">
              <Chip active={category === "all"} onClick={() => setCategory("all")} testId="category-filter-all">All categories</Chip>
              {categories.map((c) => (
                <Chip key={c} active={category === c} onClick={() => setCategory(c)} testId={`category-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {c}
                </Chip>
              ))}
            </div>
          </div>
          <motion.div layout className="mt-14 grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} p={p} i={i} />
              ))}
            </AnimatePresence>
          </motion.div>
          {!loading && filtered.length === 0 && (
            <p data-testid="products-empty" className="py-20 text-center font-display text-2xl text-teal/70">
              No products match this filter yet.
            </p>
          )}
        </div>
      </section>
      <ContactCTA />
    </main>
  );
}
